import { useCallback, useEffect, useMemo, useState, type JSX } from 'react'
import {
  ApiError,
  api,
  can,
  codeWords,
  daysUntil,
  refusalReason,
  shortDate,
  type Questionnaire,
  type ReassessmentRow,
  type TierIssuing,
  type TriggerEvent,
} from '../lib/api'
import { mailWent, mailWords } from '../lib/mail'
import { AssessTabs } from '../components/assess/AssessTabs'
import { useSave } from '../lib/save'
import { DataTable, ErrorBox, ListCount, Loading, type Column } from '../components/DataTable'
import { Dialog, Status, type Tone } from '../components/kit'
import { ReasonTip } from '../components/vendor/ReasonTip'
import { DateRuleNote, DateWarnNote } from '../components/DateRuleNote'
import { certReminderWords, checkDueAfterToday, checkNextReassessment, dueWarning, todayIndia, useLeftFields } from '../lib/dateRules'
import { dueDiligenceRefusal } from '../lib/requestRules'
import { VendorChoice } from '../components/VendorChoice'
import '../styles/prose.css'
import '../styles/studio.css'
import '../styles/sweep.css'
import '../styles/reassess.css'

// Reassessment: every vendor, when it was last asked, when it is next due, and
// a Reassess button on each.
//
// # WHY A LIST OF VENDORS AND NOT THE TIER CARDS
//
// The product officer's review of 17 September 2026 (observation 3.14): the
// screen opened on tier configuration, which is set once and then left, and
// the thing a person comes here to do, ask one vendor again, was not on it.
// The tier defaults are in Settings, Reassessment, with the same rules. What is
// here is the work: the vendors, and per vendor the two choices, now or on a
// schedule.
//
// # WHAT NOTHING HERE CAN DO THAT THE PRODUCT COULD NOT BEFORE
//
// Reassess now is the single-vendor invitation (POST /v1/vendors/{id}/reassess)
// with a reason added, so the second-person rule and the refusal when one is
// already open are the invitation's own. Schedule saves the vendor's own
// reassessment policy through the route the vendor record's policy panel uses,
// keeping every other part of that policy as it was. The full policy panel is
// still on the vendor record, one click from the vendor's name.

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))
}

// The frequencies a person picks from. Months, because that is what the
// policy stores; the tier default is "no frequency of its own".
const FREQUENCIES: { months: number; word: string }[] = [
  { months: 1, word: 'Monthly' },
  { months: 3, word: 'Quarterly' },
  { months: 6, word: 'Half-yearly' },
  { months: 12, word: 'Annually' },
]

function frequencyWord(months: number | null | undefined): string {
  if (months == null || months <= 0) return 'On change only'
  const named = FREQUENCIES.find((f) => f.months === months)
  if (named) return named.word
  if (months === 24) return 'Every two years'
  return 'Every ' + months + ' months'
}

const STATE: Record<string, { word: string; tone: Tone }> = {
  OVERDUE: { word: 'Overdue', tone: 'crit' },
  DUE_SOON: { word: 'Due soon', tone: 'warn' },
  IN_FLIGHT: { word: 'In flight', tone: 'info' },
  SCHEDULED: { word: 'Scheduled', tone: 'ok' },
  PAUSED: { word: 'Paused', tone: 'neutral' },
  ON_CHANGE: { word: 'On change', tone: 'neutral' },
}

// The order a reader works in: what is late, what is close, what is out.
const VIEWS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'OVERDUE', label: 'Overdue' },
  { key: 'DUE_SOON', label: 'Due soon' },
  { key: 'IN_FLIGHT', label: 'In flight' },
  { key: 'PAUSED', label: 'Paused' },
  { key: 'ON_CHANGE', label: 'On change' },
]

const STATE_ORDER = ['OVERDUE', 'DUE_SOON', 'IN_FLIGHT', 'SCHEDULED', 'PAUSED', 'ON_CHANGE']

export function Sustain(): JSX.Element {
  const [rows, setRows] = useState<ReassessmentRow[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [questionnaires, setQuestionnaires] = useState<Questionnaire[]>([])
  const [issuing, setIssuing] = useState<TierIssuing | null>(null)
  const [view, setView] = useState('')
  const [q, setQ] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  // What the last action did. A reassessment the API issued with mail muted
  // or switched off is recorded and not emailed, and says so in its own tone.
  const [done, setDone] = useState('')
  const [doneWent, setDoneWent] = useState(true)

  const load = useCallback(() => {
    api
      .reassessmentRoster()
      .then((r) => {
        setRows(r.vendors)
        setError(null)
      })
      .catch(setError)
  }, [])

  useEffect(load, [load])

  // What the dialog offers. Neither is needed to read the list, so a failure
  // here costs the preselection and the approval wording, not the screen.
  useEffect(() => {
    // Both are the programme's configuration; a reader who does not hold
    // config.read (the governance steward) has no dialog to fill in anyway.
    if (!can('config.read')) return
    api
      .questionnaires()
      .then((r) => setQuestionnaires(r.questionnaires))
      .catch(() => undefined)
    api
      .tierProfiles()
      .then((r) => setIssuing(r.issuing ?? null))
      .catch(() => undefined)
  }, [])

  const counts = useMemo(() => {
    const c: Record<string, number> = {}
    for (const r of rows ?? []) c[r.state] = (c[r.state] ?? 0) + 1
    return c
  }, [rows])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (rows ?? []).filter(
      (r) =>
        (view === '' || r.state === view) &&
        (needle === '' || r.vendorName.toLowerCase().includes(needle) || r.vendorCode.toLowerCase().includes(needle)),
    )
  }, [rows, view, q])

  const open = rows?.find((r) => r.vendorId === openId) ?? null

  const columns: Column<ReassessmentRow>[] = [
    {
      key: 'vendor',
      header: 'Vendor',
      sortValue: (r) => r.vendorName,
      render: (r) => (
        <>
          <a className="cell-primary" href={'#/vendor/' + encodeURIComponent(r.vendorId)}>
            {r.vendorName}
          </a>
          <span className="cell-sub mono">{r.vendorCode}</span>
        </>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      sortValue: (r) => (r.inherentTier == null ? null : -r.inherentTier),
      render: (r) => (r.tierLabel ? r.tierLabel : <span className="muted">No tier</span>),
    },
    {
      key: 'last',
      header: 'Last assessed',
      sortValue: (r) => r.lastAssessedOn,
      render: (r) => (r.lastAssessedOn ? shortDate(r.lastAssessedOn) : <span className="muted">Never</span>),
    },
    {
      key: 'next',
      header: 'Next due',
      sortValue: (r) => r.nextDueOn,
      render: (r) => <NextDue row={r} />,
    },
    {
      key: 'frequency',
      header: 'Frequency',
      sortValue: (r) => r.cadenceMonths ?? 999,
      render: (r) => (
        <>
          <span className="cell-primary">{frequencyWord(r.cadenceMonths)}</span>
          <span className="cell-sub">
            {r.cadenceSource === 'VENDOR_OVERRIDE' ? 'set for this vendor' : r.cadenceSource === 'TIER_DEFAULT' ? 'tier default' : 'no default'}
          </span>
        </>
      ),
    },
    {
      key: 'state',
      header: 'State',
      sortValue: (r) => STATE_ORDER.indexOf(r.state),
      render: (r) => {
        const s = STATE[r.state]
        return (
          <Status tone={s?.tone ?? 'neutral'} title={r.isPaused ? r.pausedReason : r.nextDueBasis}>
            {s?.word ?? codeWords(r.state)}
          </Status>
        )
      },
    },
    {
      key: 'act',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="ra-row-btn"
          onClick={() => {
            setDone('')
            setOpenId(r.vendorId)
          }}
        >
          Reassess
        </button>
      ),
    },
  ]

  return (
    <>
      <AssessTabs at="sustain" />
      <div className="page-head">
        <div>
          <h1>Reassessment</h1>
        </div>
      </div>

      {done && (
        <p className="prose-line" role="status">
          {doneWent ? <Status tone="ok">Done</Status> : <Status tone="warn">Not emailed</Status>}
          {done}
        </p>
      )}

      {error != null && <ErrorBox error={error} what="The reassessment list" />}
      {rows === null && error == null && <Loading what="the reassessment list" />}

      {rows && (
        <section className="panel panel-flush" aria-labelledby="ra-list-heading">
          <div className="prose-panelhead">
            <h2 id="ra-list-heading" className="vh">
              Vendors
            </h2>
            <span className="prose-seg" role="group" aria-label="Show">
              {VIEWS.map((v) => (
                <button
                  key={v.key || 'all'}
                  type="button"
                  className="prose-seg-btn"
                  aria-pressed={view === v.key}
                  onClick={() => setView(v.key)}
                >
                  {v.label}
                  <b>{v.key === '' ? rows.length : (counts[v.key] ?? 0)}</b>
                </button>
              ))}
            </span>
            <span className="prose-grow" />
            <label className="vh" htmlFor="ra-search">
              Find a vendor
            </label>
            <input
              id="ra-search"
              className="ra-search"
              type="search"
              placeholder="Find a vendor"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <DataTable
            caption="Vendors with when they were last assessed, when they are next due, how often and where they stand."
            columns={columns}
            rows={shown}
            rowKey={(r) => r.vendorId}
            defaultSort={{ key: 'state', dir: 'asc' }}
            empty={{
              title: rows.length === 0 ? 'No vendor on the register yet' : 'No vendor matches',
              detail: rows.length === 0 ? 'Vendors appear here once they are added.' : 'Choose All, or clear the search.',
            }}
            footer={<ListCount shown={shown.length} total={rows.length} noun="vendors" />}
          />
        </section>
      )}

      <TriggerQueue vendors={rows ?? []} />

      <ReassessDialog
        row={open}
        questionnaires={questionnaires}
        issuing={issuing}
        onClose={() => setOpenId(null)}
        onDone={(words, went = true) => {
          setOpenId(null)
          setDone(words)
          setDoneWent(went)
          load()
        }}
      />
    </>
  )
}

function NextDue({ row }: { row: ReassessmentRow }): JSX.Element {
  if (row.openAssessmentReference) {
    return (
      <>
        <span className="cell-primary">{row.openAssessmentDueOn ? 'Answer due ' + shortDate(row.openAssessmentDueOn) : 'Open now'}</span>
        <span className="cell-sub mono">{row.openAssessmentReference}</span>
      </>
    )
  }
  if (!row.nextDueOn) return <span className="muted">Not scheduled</span>
  const days = daysUntil(row.nextDueOn)
  return (
    <>
      <span className="cell-primary">{shortDate(row.nextDueOn)}</span>
      {days != null && days < 0 && <span className="cell-sub ra-late">{Math.abs(days) + ' days late'}</span>}
      {row.nextDueSource === 'CHOSEN' && <span className="cell-sub">Date chosen</span>}
    </>
  )
}

// ------------------------------------------------------------ the pop-up

type Choice = 'now' | 'schedule'

function isoIn(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() + days)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return d.getFullYear() + '-' + m + '-' + day
}

// Two choices for one vendor, the way the officer asked for them: ask now, with
// a reason, or change how often the vendor is asked.
function ReassessDialog({
  row,
  questionnaires,
  issuing,
  onClose,
  onDone,
}: {
  row: ReassessmentRow | null
  questionnaires: Questionnaire[]
  issuing: TierIssuing | null
  onClose: () => void
  onDone: (words: string, went?: boolean) => void
}): JSX.Element {
  const [choice, setChoice] = useState<Choice>('now')
  const [reason, setReason] = useState('')
  const [questionnaire, setQuestionnaire] = useState('')
  const [dueOn, setDueOn] = useState(isoIn(14))
  const [months, setMonths] = useState('')
  // The next reassessment on a date a person chose, beside the frequency.
  // Empty means none: the date follows from the frequency.
  const [scheduleOn, setScheduleOn] = useState('')
  const save = useSave(row ? row.vendorId : null, row ? 'Reassess ' + row.vendorName : 'Reassess')
  const busy = save.busy
  const [failed, setFailed] = useState<ApiError | null>(null)
  const [tried, setTried] = useState(false)
  // D11 and D14 of the date rules, said on the field once the person leaves it.
  const left = useLeftFields()
  const dueRule = checkDueAfterToday('Answer-by date', dueOn, todayIndia())
  const scheduleRule =
    scheduleOn !== (row?.nextDueChosen?.on ?? '') ? checkNextReassessment(scheduleOn, row?.lastAssessedOn ?? '', todayIndia()) : ''

  // Fresh for each vendor: the tier's questionnaire preselected, and the
  // frequency showing what is set now.
  useEffect(() => {
    if (!row) return
    setChoice('now')
    setReason('')
    setQuestionnaire(row.tierQuestionnaireCode ?? '')
    setDueOn(isoIn(14))
    setMonths(row.cadenceSource === 'VENDOR_OVERRIDE' && row.cadenceMonths != null ? String(row.cadenceMonths) : '')
    setScheduleOn(row.nextDueChosen?.on ?? '')
    setFailed(null)
    setTried(false)
    left.reset()
  }, [row, left.reset])

  const publishable = questionnaires.filter((x) => x.publishedVersionId)
  const inFlight = row?.openAssessmentReference
  // The gate every questionnaire path asks (release 3.3), with the reason this
  // screen always asks for: a vendor it refuses is said so, with the way
  // forward, rather than offered a button the server refuses.
  const refused = row ? dueDiligenceRefusal({ status: row.status, requestApproved: row.requestApproved }, 'asked on this screen') : null
  const waits =
    issuing?.singleInviteNeedsApprover === true ||
    (row?.inherentTier != null && (issuing?.tiersNeedingSecondPerson ?? []).includes(row.inherentTier))

  function reassessNow() {
    if (!row) return
    setTried(true)
    left.leave('dueOn')
    if (reason.trim() === '' || dueRule) return
    setFailed(null)
    save.send(
      api.reassessVendor(row.vendorId, {
        reason: reason.trim(),
        questionnaireCode: questionnaire || undefined,
        dueOn: dueOn || undefined,
      }),
      {
        done: (r) => {
          if (r.status === 'DRAFT') return onDone(row.vendorName + ' is waiting for a second person to approve it.')
          // Sent only when the API says mail will go. See lib/mail.ts.
          if (!mailWent(r)) return onDone(row.vendorName + ': ' + mailWords(r), false)
          onDone(row.vendorName + ' has been sent the questionnaire' + (r.assessment ? ', due ' + shortDate(r.assessment.dueOn) : '') + '.')
        },
        failed: (err) => setFailed(asApiError(err)),
      },
    )
  }

  // The policy is saved whole, as the vendor record's panel saves it, with
  // only the frequency and the date changed. Sending the frequency alone would
  // reset the mode, the suppression window, the event triggers and a pause.
  function schedule() {
    if (!row) return
    if (scheduleRule) {
      left.leave('scheduleOn')
      return
    }
    setFailed(null)
    const work = api.reassessmentPolicy(row.vendorId).then((view) => {
        const ov = (view.override ?? null) as { eventTriggers?: unknown } | null
        return api.saveReassessmentPolicy(row.vendorId, {
          cadenceMonths: months === '' ? null : Number(months),
          defaultMode: view.resolved.defaultMode,
          suppressionDays: view.resolved.suppressionDays,
          eventTriggers: ov?.eventTriggers,
          isPaused: view.resolved.isPaused,
          pausedReason: view.resolved.pausedReason ?? '',
          nextReassessmentOn: scheduleOn,
        })
    })
    save.send(work, {
      done: (view) =>
        onDone(
          row.vendorName +
            ' is now ' +
            frequencyWord(view.resolved.cadenceMonths).toLowerCase() +
            (view.resolved.nextDueOn ? ', next due ' + shortDate(view.resolved.nextDueOn) : '') +
            '.',
        ),
      failed: (err) => setFailed(asApiError(err)),
    })
  }

  const tierDefault = row ? frequencyWord(row.tierCadenceMonths) : ''
  const canNow = can('questionnaire.issue')
  const canPlan = can('assessment.plan')

  return (
    <Dialog
      shape="modal"
      kind="form"
      open={row !== null}
      onClose={onClose}
      title={row ? 'Reassess ' + row.vendorName : 'Reassess'}
      busy={busy}
      problems={choice === 'now' && !inFlight && tried && reason.trim() === '' ? 1 : 0}
      primary={
        !row
          ? undefined
          : choice === 'now'
            ? {
                label: busy ? 'Sending' : waits ? 'Send for approval' : 'Reassess now',
                off: !canNow
                  ? refusalReason('questionnaire.issue')
                  : inFlight
                    ? 'This vendor already has an open assessment'
                    : refused != null && refused.message,
                onClick: reassessNow,
                actionId: 'tprm.sustain.a01',
              }
            : {
                label: busy ? 'Saving' : 'Save schedule',
                off: !canPlan && refusalReason('assessment.plan'),
                onClick: schedule,
                actionId: 'tprm.sustain.a02',
              }
      }
    >
      {row && (
        <>
          <div className="ra-choices" role="radiogroup" aria-label="How">
            <button
              type="button"
              role="radio"
              aria-checked={choice === 'now'}
              className={'ra-choice' + (choice === 'now' ? ' on' : '')}
              onClick={() => setChoice('now')}
            >
              <strong>Reassess now</strong>
              <span>Send the questionnaire today</span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={choice === 'schedule'}
              className={'ra-choice' + (choice === 'schedule' ? ' on' : '')}
              onClick={() => setChoice('schedule')}
            >
              <strong>Schedule reassessment</strong>
              <span>How often, or on a date</span>
            </button>
          </div>

          {failed && <ErrorBox error={failed} what={choice === 'now' ? 'The reassessment' : 'The schedule'} verb="saved" />}

          {choice === 'now' ? (
            refused ? (
              <p className="note">
                {refused.message}{' '}
                <a href={'#/vendor/' + row.vendorId}>Open the vendor</a>
              </p>
            ) : inFlight ? (
              <p className="note">
                This vendor already has an open assessment, reference <span className="mono">{inFlight}</span>. Send a
                reminder for that one from the vendor's next step rather than issuing a second.
              </p>
            ) : (
              <div className="ra-form">
                <div className="field">
                  <label htmlFor="ra-reason">Reason</label>
                  <textarea
                    id="ra-reason"
                    rows={3}
                    maxLength={1000}
                    value={reason}
                    placeholder="Why this vendor is being asked again now"
                    aria-invalid={tried && reason.trim() === ''}
                    aria-describedby={tried && reason.trim() === '' ? 'ra-reason-need' : undefined}
                    onChange={(e) => setReason(e.target.value)}
                  />
                  {tried && reason.trim() === '' && (
                    <p className="field-error" id="ra-reason-need">
                      Say why, it goes on the record
                    </p>
                  )}
                </div>
                <div className="field">
                  <label htmlFor="ra-questionnaire">Questionnaire</label>
                  <select id="ra-questionnaire" value={questionnaire} onChange={(e) => setQuestionnaire(e.target.value)}>
                    {!row.tierQuestionnaireCode && <option value="">Choose one</option>}
                    {publishable.map((x) => (
                      <option key={x.id} value={x.code}>
                        {x.name || x.code}
                        {x.code === row.tierQuestionnaireCode ? ' (tier default)' : ''}
                      </option>
                    ))}
                    {row.tierQuestionnaireCode && !publishable.some((x) => x.code === row.tierQuestionnaireCode) && (
                      <option value={row.tierQuestionnaireCode}>{row.tierQuestionnaireCode} (tier default)</option>
                    )}
                  </select>
                </div>
                <div className="field">
                  <label htmlFor="ra-due">Answer by</label>
                  <input
                    id="ra-due"
                    type="date"
                    min={isoIn(1)}
                    value={dueOn}
                    onChange={(e) => setDueOn(e.target.value)}
                    onBlur={() => left.leave('dueOn')}
                    aria-invalid={left.shown('dueOn', dueRule) ? true : undefined}
                    aria-describedby={left.shown('dueOn', dueRule) ? 'ra-due-rule' : undefined}
                  />
                  <DateRuleNote id="ra-due-rule" message={left.shown('dueOn', dueRule)} />
                  <DateWarnNote id="ra-due-warn" message={left.shown('dueOn', dueRule ? '' : dueWarning(dueOn, '', todayIndia()))} />
                </div>
                {waits && <p className="muted ra-note">A second person approves it before it goes out.</p>}
                {!canNow && (
                  <p className="field-note sw-row">
                    <Status tone="neutral">Read only</Status>
                    <ReasonTip label="Why you cannot send" text={refusalReason('questionnaire.issue')} />
                  </p>
                )}
              </div>
            )
          ) : (
            <div className="ra-form">
              <div className="field">
                <label htmlFor="ra-frequency">Frequency</label>
                <select id="ra-frequency" value={months} onChange={(e) => setMonths(e.target.value)}>
                  {FREQUENCIES.map((f) => (
                    <option key={f.months} value={String(f.months)}>
                      {f.word}
                    </option>
                  ))}
                  <option value="">Tier default ({tierDefault.toLowerCase()})</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="ra-on">Next reassessment on (optional)</label>
                <input
                  id="ra-on"
                  type="date"
                  min={isoIn(0)}
                  value={scheduleOn}
                  onChange={(e) => setScheduleOn(e.target.value)}
                  onBlur={() => left.leave('scheduleOn')}
                  aria-invalid={left.shown('scheduleOn', scheduleRule) ? true : undefined}
                  aria-describedby={left.shown('scheduleOn', scheduleRule) ? 'ra-on-rule' : undefined}
                />
                <DateRuleNote id="ra-on-rule" message={left.shown('scheduleOn', scheduleRule)} />
              </div>
              <p className="muted ra-note">
                {scheduleOn
                  ? 'The date wins until it passes, then the frequency applies again.'
                  : 'The next date follows from the last assessment.'}
              </p>
              {!canPlan && (
                <p className="field-note sw-row">
                  <Status tone="neutral">Read only</Status>
                  <ReasonTip label="Why you cannot save" text={refusalReason('assessment.plan')} />
                </p>
              )}
              <p className="ra-more">
                <a href={'#/vendor/' + encodeURIComponent(row.vendorId) + '/full/reassessment'}>Full policy on the vendor record</a>
              </p>
            </div>
          )}
        </>
      )}
    </Dialog>
  )
}

// --------------------------------------------------------- trigger queue

// The six statuses the CHECK on trigger_events.status in migration 0006
// allows, and no others. This map used to carry ACTIONED, which the database
// refuses, and none of QUEUED, CAMPAIGNED or SUPPRESSED, which it writes.
// Pending is the one that waits on somebody; the rest are under way or
// finished and go quiet. CANCELLED reads as Dismissed, the word the filter
// uses for it.
const TRIGGER_STATUS: Record<string, { word: string; tone: Tone }> = {
  PENDING: { word: 'Pending', tone: 'warn' },
  QUEUED: { word: 'Queued', tone: 'info' },
  CAMPAIGNED: { word: 'In a campaign', tone: 'neutral' },
  SUPPRESSED: { word: 'Suppressed', tone: 'plain' },
  MERGED: { word: 'Merged', tone: 'neutral' },
  CANCELLED: { word: 'Dismissed', tone: 'plain' },
}

// The schedule's rows as the vendor picker takes them. They are what the
// select offered, so they are what the picker offers: a vendor with no
// schedule row could not be chosen before and cannot be chosen now.
export function triggerVendors(rows: Pick<ReassessmentRow, 'vendorId' | 'vendorCode' | 'vendorName'>[]) {
  return rows.map((v) => ({ id: v.vendorId, vendorCode: v.vendorCode, legalName: v.vendorName, displayName: '' }))
}

// Under the list and compact: a trigger is something that moved, raised on a
// cadence, a contract renewal or by hand, and the list above is where it is
// answered. Raising one by hand sits behind a disclosure because it is rare.
function TriggerQueue({ vendors }: { vendors: ReassessmentRow[] }) {
  const [status, setStatus] = useState('PENDING')
  const [list, setList] = useState<TriggerEvent[] | null>(null)
  const [overdue, setOverdue] = useState(0)
  const [note, setNote] = useState('')
  const [error, setError] = useState<unknown>(null)

  const [raising, setRaising] = useState(false)
  const [raiseError, setRaiseError] = useState<ApiError | null>(null)
  const [newVendor, setNewVendor] = useState('')
  const [newType, setNewType] = useState('CONTRACT_RENEWAL')
  const [newDue, setNewDue] = useState('')

  const [dismissing, setDismissing] = useState<string | null>(null)
  const [dismissReason, setDismissReason] = useState('')

  const choices = useMemo(() => triggerVendors(vendors), [vendors])

  const load = useCallback(() => {
    api
      .triggers(status)
      .then((r) => {
        setList(r.triggers)
        setOverdue(r.overdue)
        setNote(r.note)
        setError(null)
      })
      .catch(setError)
  }, [status])

  useEffect(load, [load])

  function raise() {
    setRaising(true)
    setRaiseError(null)
    api
      .raiseTrigger({ vendorId: newVendor, triggerType: newType, dueOn: newDue })
      .then(() => {
        setNewVendor('')
        setNewDue('')
        load()
      })
      .catch((err) => setRaiseError(asApiError(err)))
      .finally(() => setRaising(false))
  }

  function dismiss(id: string) {
    api
      .dismissTrigger(id, dismissReason)
      .then(() => {
        setDismissing(null)
        setDismissReason('')
        load()
      })
      .catch((err) => setRaiseError(asApiError(err)))
  }

  return (
    <section className="panel ra-triggers">
      <div className="section-head">
        <h2 className="sw-row">
          Trigger queue <ReasonTip label="About the trigger queue" text={note} />
        </h2>
        <div className="field">
          <label htmlFor="trigger-status" className="vh">
            Show
          </label>
          <select id="trigger-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="PENDING">Pending</option>
            <option value="MERGED">Merged</option>
            {/* CANCELLED, not DISMISSED. Dismissing a trigger writes CANCELLED
                and the CHECK in 0006 has no DISMISSED, so this filter matched
                nothing and the view was permanently empty however many triggers
                had been dismissed. */}
            <option value="CANCELLED">Dismissed</option>
            <option value="ALL">Everything</option>
          </select>
        </div>
      </div>

      {raiseError && <ErrorBox error={raiseError} what="The trigger" />}
      {error != null && <ErrorBox error={error} what="The trigger queue" />}

      {!list ? (
        <Loading what="triggers" />
      ) : list.length === 0 ? (
        <p className="muted ra-quiet">Nothing {status === 'ALL' ? 'here' : TRIGGER_STATUS[status]?.word.toLowerCase() ?? 'here'}</p>
      ) : (
        <ul className="exc-list">
          {list.map((t) => {
            const days = daysUntil(t.dueOn)
            const late = days !== null && days < 0
            return (
              <li className={late ? 'exc trigger-overdue' : 'exc'} key={t.id}>
                <div className="exc-head">
                  <span className="exc-title">
                    <a href={'#/vendor/' + encodeURIComponent(t.vendorId)}>
                      <span className="mono">{t.vendorCode}</span> {t.vendorName}
                    </a>
                  </span>
                  <Status tone={TRIGGER_STATUS[t.status]?.tone ?? 'neutral'} title={t.status}>
                    {TRIGGER_STATUS[t.status]?.word ?? codeWords(t.status)}
                  </Status>
                </div>
                <div className="exc-meta">
                  <span>{codeWords(t.triggerType)}</span>
                  {certReminderWords(t.triggerType, t.detail) && <span>{certReminderWords(t.triggerType, t.detail)}</span>}
                  <span>
                    due {shortDate(t.dueOn)}
                    {days !== null && (
                      <>
                        {' '}
                        {late ? (
                          <Status tone="crit">{Math.abs(days) + ' days late'}</Status>
                        ) : (
                          <span className="muted">{'in ' + days + ' days'}</span>
                        )}
                      </>
                    )}
                  </span>
                  <span className="muted">
                    raised by {t.raisedBy} on {shortDate(t.raisedAt)}
                  </span>
                </div>

                {t.affectedSectionKeys?.length > 0 && (
                  <p className="muted">
                    Only these sections need reanswering:{' '}
                    <span className="mono">{t.affectedSectionKeys.join(', ')}</span>
                  </p>
                )}
                {t.reason && <p className="why">{t.reason}</p>}
                <TriggerDetail detail={t.detail} />

                {t.status === 'PENDING' && (
                  <div className="exc-actions">
                    {dismissing === t.id ? (
                      <>
                        <label htmlFor={'dismiss-' + t.id} className="vh">
                          Why this trigger is being dismissed
                        </label>
                        <input
                          id={'dismiss-' + t.id}
                          type="text"
                          placeholder="Why this does not need a reassessment"
                          value={dismissReason}
                          onChange={(e) => setDismissReason(e.target.value)}
                        />
                        <button
                          type="button"
                          className="danger"
                          data-action="tprm.sustain.a04"
                          disabled={!dismissReason.trim()}
                          onClick={() => dismiss(t.id)}
                        >
                          Dismiss
                        </button>
                        <button type="button" onClick={() => setDismissing(null)}>
                          Keep it
                        </button>
                      </>
                    ) : (
                      <>
                        <a className="linkish" href="#/campaigns?part=raise">
                          Raise a campaign
                        </a>
                        <button
                          type="button"
                          onClick={() => setDismissing(t.id)}
                          disabled={!can('assessment.plan')}
                          title={can('assessment.plan') ? undefined : refusalReason('assessment.plan')}
                        >
                          Dismiss
                        </button>
                      </>
                    )}
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {list && list.length > 0 && (
        <ListCount shown={list.length} noun="triggers">
          {overdue > 0 ? ', ' + overdue + ' overdue' : ''}
        </ListCount>
      )}

      <details className="ra-raise">
        <summary>Raise one by hand</summary>
        <div className="pickrow">
          <div className="field studio-grow">
            <label htmlFor="new-trigger-vendor">Vendor</label>
            <VendorChoice bare browse id="new-trigger-vendor" label="Vendor" value={newVendor} options={choices} onChange={setNewVendor} />
          </div>
          <div className="field">
            <label htmlFor="new-trigger-type">Because</label>
            <select id="new-trigger-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {/* The nine values migration 0006 permits, and no others. This
                  list used to offer SCOPE_CHANGE, INCIDENT, REGULATORY and
                  CADENCE, none of which exist: four of the six options returned
                  422 with a message naming values the reader could not see. */}
              <option value="SCHEDULE">Scheduled cadence</option>
              <option value="CONTRACT_RENEWAL">Contract renewal</option>
              <option value="CERT_EXPIRY">Certificate expiring</option>
              <option value="TIER_INCREASE">Tier increased</option>
              <option value="INCIDENT_REPORTED">Incident reported</option>
              <option value="DATA_CATEGORY_ADDED">New data category</option>
              <option value="SUBCONTRACTOR_CHANGE">Subcontractor changed</option>
              <option value="ADVERSE_MEDIA">Adverse media</option>
              <option value="MANUAL">Somebody asked</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="new-trigger-due">Due on</label>
            <input id="new-trigger-due" type="date" value={newDue} onChange={(e) => setNewDue(e.target.value)} />
          </div>
          <button
            type="button"
            data-action="tprm.sustain.a03"
            onClick={raise}
            disabled={raising || !newVendor || !newDue || !can('assessment.plan')}
            title={can('assessment.plan') ? undefined : refusalReason('assessment.plan')}
          >
            {raising ? 'Raising' : 'Raise'}
          </button>
        </div>
      </details>
    </section>
  )
}

// The detail blob is written by whichever thing raised the trigger, so it has no
// fixed shape. Rendering the keys it does have beats rendering json at somebody.
function TriggerDetail({ detail }: { detail: unknown }) {
  if (!detail || typeof detail !== 'object') return null
  const entries = Object.entries(detail as Record<string, unknown>).filter(
    ([, v]) => v !== null && v !== undefined && String(v) !== '',
  )
  if (entries.length === 0) return null
  return (
    <dl className="kv">
      {entries.map(([k, v]) => (
        <div key={k}>
          <dt>{k.replace(/_/g, ' ')}</dt>
          <dd className={k.endsWith('_id') || k.endsWith('Id') ? 'mono' : undefined}>{String(v)}</dd>
        </div>
      ))}
    </dl>
  )
}
